import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AuthService } from '../core/auth/auth.service';

interface BreadcrumbItem {
  label: string;
  path?: string;
}

const BREADCRUMB_MAP: Record<string, BreadcrumbItem[]> = {
  '/dashboard':       [{ label: 'Home' }],
  '/about':           [{ label: 'Home', path: '/dashboard' }, { label: 'About' }],
  '/contact':         [{ label: 'Home', path: '/dashboard' }, { label: 'Contact' }],
  '/contacts':        [{ label: 'Home', path: '/dashboard' }, { label: 'My Contacts' }],
  '/institutions':    [{ label: 'Home', path: '/dashboard' }, { label: 'My Linked Institutions' }],
  '/billing':         [{ label: 'Home', path: '/dashboard' }, { label: 'Billing' }],
  '/payment-details': [{ label: 'Home', path: '/dashboard' }, { label: 'Billing', path: '/billing' }, { label: 'Payment Details' }],
  '/account':         [{ label: 'Home', path: '/dashboard' }, { label: 'My Account' }],
  '/help':            [{ label: 'Home', path: '/dashboard' }, { label: 'Get Help' }],
  '/services':        [{ label: 'Home', path: '/dashboard' }, { label: 'Other Services' }],
  '/orders':          [{ label: 'Home', path: '/dashboard' }, { label: 'Orders' }],
  '/checkout':        [{ label: 'Home', path: '/dashboard' }, { label: 'Orders', path: '/orders' }, { label: 'Checkout' }],
};

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './app-breadcrumb.component.html',
  styleUrls: ['./app-breadcrumb.component.scss']
})
export class BreadcrumbComponent {
  private readonly router = inject(Router);
  readonly crumbs = signal<BreadcrumbItem[]>([]);

  constructor() {
    this.setCrumbs(this.router.url);
    this.router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed()
    ).subscribe(e => this.setCrumbs(e.urlAfterRedirects));
  }

  private setCrumbs(url: string): void {
    const path = url.split('?')[0];
    this.crumbs.set(BREADCRUMB_MAP[path] ?? [{ label: 'Home', path: '/dashboard' }]);
  }
}

@Component({
  selector: 'app-home-shell',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, RouterOutlet, BreadcrumbComponent],
  templateUrl: './app-home-shell.component.html',
  styleUrls: ['./app-home-shell.component.scss']
})
export class HomeShellComponent implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  readonly isMenuOpen = signal(false);
  readonly currentYear = new Date().getFullYear();

  constructor() {
    effect(() => {
      if (typeof window === 'undefined') {
        return;
      }

      const shouldLockScroll = this.isMenuOpen() && window.matchMedia('(max-width: 1099px)').matches;
      document.body.classList.toggle('nav-menu-open', shouldLockScroll);
    });
  }

  toggleMenu(): void {
    this.isMenuOpen.update((isOpen) => !isOpen);
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  logout(): void {
    this.closeMenu();
    this.authService.logout();
    void this.router.navigateByUrl('/login');
  }

  ngOnDestroy(): void {
    document.body.classList.remove('nav-menu-open');
  }
}

@Component({
  selector: 'app-dashboard-page',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './app-dashboard-page.component.html',
  styleUrls: ['./app-dashboard-page.component.scss']
})
export class DashboardPageComponent {
  private readonly authService = inject(AuthService);
  readonly userName = computed(() => this.authService.currentUser()?.fullName ?? 'Member');
}

@Component({
  selector: 'app-about-page',
  standalone: true,
  templateUrl: './app-about-page.component.html',
  styleUrls: ['./app-about-page.component.scss']
})
export class AboutPageComponent {}

@Component({
  selector: 'app-contact-page',
  standalone: true,
  templateUrl: './app-contact-page.component.html',
  styleUrls: ['./app-contact-page.component.scss']
})
export class ContactPageComponent {}